import { useState } from "react";
import { doc, getDoc, updateDoc, addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { awardPoints } from "./Expenses";

const COUNTRY_CODES = {
  "Afghanistan": "004", "Albania": "008", "Algeria": "012", "Angola": "024",
  "Argentina": "032", "Australia": "036", "Austria": "040", "Bangladesh": "050",
  "Belgium": "056", "Bolivia": "068", "Brazil": "076", "Bulgaria": "100",
  "Cambodia": "116", "Cameroon": "120", "Canada": "124", "Chile": "152",
  "China": "156", "Colombia": "170", "Costa Rica": "188", "Croatia": "191",
  "Cuba": "192", "Czech Republic": "203", "Denmark": "208", "Ecuador": "218",
  "Egypt": "818", "Ethiopia": "231", "Finland": "246", "France": "250",
  "Germany": "276", "Ghana": "288", "Greece": "300", "Guatemala": "320",
  "Hungary": "348", "India": "356", "Indonesia": "360", "Iran": "364",
  "Iraq": "368", "Ireland": "372", "Israel": "376", "Italy": "380",
  "Jamaica": "388", "Japan": "392", "Jordan": "400", "Kenya": "404",
  "South Korea": "410", "Kuwait": "414", "Laos": "418", "Lebanon": "422",
  "Malaysia": "458", "Mexico": "484", "Morocco": "504", "Nepal": "524",
  "Netherlands": "528", "New Zealand": "554", "Nigeria": "566", "Norway": "578",
  "Pakistan": "586", "Panama": "591", "Peru": "604", "Philippines": "608",
  "Poland": "616", "Portugal": "620", "Qatar": "634", "Romania": "642",
  "Russia": "643", "Saudi Arabia": "682", "Senegal": "686", "Singapore": "702",
  "Slovakia": "703", "South Africa": "710", "Spain": "724", "Sri Lanka": "144",
  "Sweden": "752", "Switzerland": "756", "Taiwan": "158", "Thailand": "764",
  "Turkey": "792", "Uganda": "800", "Ukraine": "804", "United Arab Emirates": "784",
  "United Kingdom": "826", "United States": "840", "Uruguay": "858",
  "Venezuela": "862", "Vietnam": "704", "Yemen": "887", "Zimbabwe": "716",
};

const CATEGORIES = ["Food", "Culture", "Nightlife", "Nature", "Shopping", "Hidden gem", "Transport", "Accommodation"];

export default function PostTripFlow({ trip, onComplete, onSkip }) {
  const isMultiCity = !!trip.isMultiCity;

  // Build list of unique countries for multi-city trips
  const allCountries = isMultiCity
    ? [...new Set((trip.legs || []).map(l => l.country).filter(Boolean))]
    : [trip.country].filter(Boolean);

  const [step, setStep] = useState(1); // 1=welcome, 2=scratch, 3=rating, 4=tip, 5=done
  const [scratchedCountries, setScratchedCountries] = useState(new Set());
  const [scratchAllDone, setScratchAllDone] = useState(false);
  const [rating, setRating] = useState(0);
  const [poppingStar, setPoppingStar] = useState(0);
  const [submittingTip, setSubmittingTip] = useState(false);
  const [tipError, setTipError] = useState(null);
  const [scratchError, setScratchError] = useState(null);
  const [rateError, setRateError] = useState(null);
  const [tip, setTip] = useState({
    title: "",
    tip: "",
    category: "Food",
    city: trip.destination || "",
    country: trip.country || "",
    visitedDate: trip.endDate || "",
  });

  const countryCode = COUNTRY_CODES[trip.country] || null;
  const scratched = isMultiCity ? scratchAllDone : scratchedCountries.has(trip.country);

  const scratchCountryToMap = async (countryCodes) => {
    if (!auth.currentUser) return false;
    try {
      const vcSnap = await getDocs(
        query(collection(db, "visitedCountries"), where("uid", "==", auth.currentUser.uid))
      );
      if (!vcSnap.empty) {
        const vcDoc = vcSnap.docs[0];
        const current = vcDoc.data().countries || [];
        const merged = [...new Set([...current, ...countryCodes])];
        await updateDoc(doc(db, "visitedCountries", vcDoc.id), { countries: merged });
      } else {
        await addDoc(collection(db, "visitedCountries"), {
          uid: auth.currentUser.uid,
          countries: countryCodes,
        });
      }
      return true;
    } catch (err) {
      console.error(err);
      setScratchError("Couldn't update your scratch map. Your trip was saved.");
      return false;
    }
  };

  const handleScratch = async () => {
    if (!countryCode) { setScratchedCountries(new Set([trip.country])); return; }
    const ok = await scratchCountryToMap([countryCode]);
    if (ok) setScratchedCountries(new Set([trip.country]));
  };

  const handleScratchOne = async (country) => {
    const code = COUNTRY_CODES[country];
    if (code) {
      const ok = await scratchCountryToMap([code]);
      if (!ok) return;
    }
    setScratchedCountries(prev => new Set([...prev, country]));
  };

  const handleScratchAll = async () => {
    const codes = allCountries.map(c => COUNTRY_CODES[c]).filter(Boolean);
    if (codes.length) {
      const ok = await scratchCountryToMap(codes);
      if (!ok) return;
    }
    setScratchedCountries(new Set(allCountries));
    setScratchAllDone(true);
  };

  const handleRate = async () => {
    if (!auth.currentUser) { setRateError("You've been signed out. Please sign in again."); return; }
    setRateError(null);
    try {
      await updateDoc(doc(db, "trips", trip.id), {
        [`rating_${auth.currentUser.uid}`]: rating,
        [`ratedBy_${auth.currentUser.uid}`]: true,
      });
      setStep(4);
    } catch (err) {
      console.error(err);
      setRateError("Couldn't save your rating. Please try again.");
    }
  };

  const handleSubmitTip = async () => {
    if (!tip.title || !tip.tip) return;
    if (!auth.currentUser) return;
    setSubmittingTip(true);
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const profile = userSnap.exists() ? userSnap.data() : {};
      await addDoc(collection(db, "tips"), {
        ...tip,
        authorId: auth.currentUser.uid,
        authorName: profile.name || "Anonymous",
        authorPhoto: profile.photoURL || null,
        likes: [],
        createdAt: new Date(),
      });
      await awardPoints(auth.currentUser.uid, 50, "Shared a local tip after trip");
      setStep(5);
    } catch (err) {
      console.error(err);
      setTipError("Failed to share tip. Please try again.");
    }
    setSubmittingTip(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .pt-root { min-height: 100vh; background: #FAF6F0; font-family: 'DM Sans', sans-serif; display: flex; flex-direction: column; }
        .pt-hero { background: #1E2D4A; padding: 48px 24px 40px; text-align: center; position: relative; overflow: hidden; }
        .pt-hero::before { content: ''; position: absolute; top: -60px; left: 50%; transform: translateX(-50%); width: 300px; height: 300px; background: radial-gradient(circle, rgba(245,166,35,0.2) 0%, transparent 70%); }
        .pt-emoji { font-size: 52px; margin-bottom: 16px; display: block; position: relative; z-index: 1; }
        .pt-hero-title { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 600; color: white; margin-bottom: 8px; line-height: 1.2; position: relative; z-index: 1; }
        .pt-hero-title em { font-style: italic; color: #F5A623; }
        .pt-hero-sub { font-size: 14px; color: rgba(255,255,255,0.5); font-weight: 300; position: relative; z-index: 1; }
        .pt-content { flex: 1; padding: 24px; }
        .pt-card { background: white; border-radius: 18px; border: 0.5px solid rgba(26,23,20,0.08); padding: 24px; margin-bottom: 14px; }
        .pt-card-title { font-size: 15px; font-weight: 500; color: #1A1714; margin-bottom: 6px; }
        .pt-card-sub { font-size: 13px; color: rgba(26,23,20,0.45); font-weight: 300; line-height: 1.5; margin-bottom: 16px; }
        .pt-scratch-btn { width: 100%; padding: 16px; background: #F5A623; color: #1E2D4A; border: none; border-radius: 14px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .pt-scratch-btn:hover { background: #C47A0A; color: white; }
        .pt-scratch-done { width: 100%; padding: 14px; background: #E1F5EE; color: #085041; border: none; border-radius: 14px; font-size: 14px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: default; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .pt-field { margin-bottom: 12px; }
        .pt-label { display: block; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.55); margin-bottom: 5px; }
        .pt-input { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1A1714; outline: none; }
        .pt-input:focus { border-color: #F5A623; background: white; }
        .pt-textarea { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1A1714; outline: none; resize: none; height: 100px; }
        .pt-textarea:focus { border-color: #F5A623; background: white; }
        .pt-categories { display: flex; flex-wrap: wrap; gap: 7px; }
        .pt-cat { padding: 7px 13px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 100px; background: white; font-size: 12px; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .pt-cat.selected { background: #FEF0DC; color: #C47A0A; border-color: #F5A623; }
        .pt-primary-btn { width: 100%; padding: 14px; background: #1E2D4A; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; margin-top: 8px; }
        .pt-primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pt-secondary-btn { width: 100%; padding: 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.45); cursor: pointer; font-family: 'DM Sans', sans-serif; margin-top: 8px; }
        .pt-points-badge { display: inline-flex; align-items: center; gap: 6px; background: #FEF0DC; border-radius: 100px; padding: 5px 12px; font-size: 12px; font-weight: 500; color: #C47A0A; margin-bottom: 14px; }
        .pt-done-icon { width: 72px; height: 72px; border-radius: 50%; background: #FEF0DC; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; font-size: 32px; }
        .pt-steps { display: flex; gap: 6px; justify-content: center; padding: 16px 0 0; }
        .pt-step-dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(26,23,20,0.15); transition: background 0.2s; }
        .pt-step-dot.active { background: #F5A623; }
        @keyframes pt-star-pop { 0%{transform:scale(1)} 40%{transform:scale(1.4)} 70%{transform:scale(0.9)} 100%{transform:scale(1)} }
        .pt-stars { display:flex; gap:8px; justify-content:center; margin:20px 0; }
        .pt-star-btn { font-size:44px; cursor:pointer; border:none; background:none; padding:4px 6px; line-height:1; color:#1E2D4A; font-family:inherit; }
        .pt-star-btn.filled { color:#F5A623; }
        .pt-star-btn.popping { animation:pt-star-pop 0.35s ease; }
      `}</style>

      <div className="pt-root">

        {/* Step 1 — Welcome back */}
        {step === 1 && (
          <>
            <div className="pt-hero">
              <span className="pt-emoji">🌍</span>
              <h1 className="pt-hero-title">Welcome back from<br /><em>{isMultiCity ? trip.name : `${trip.destination}!`}</em></h1>
              <p className="pt-hero-sub">Hope it was amazing. Let's close out your trip.</p>
            </div>
            <div className="pt-content">
              <div className="pt-card">
                <div className="pt-card-title">You just got back from {trip.name}</div>
                <div className="pt-card-sub">
                  Take 2 minutes to scratch {isMultiCity ? `${allCountries.length} countries` : trip.country} off your map and share a tip for the next traveler. You'll earn points too!
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#FAF6F0',borderRadius:10}}>
                    <span style={{fontSize:20}}>🗺️</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:'#1A1714'}}>
                        Scratch {isMultiCity ? `${allCountries.length} countries` : trip.country} off your map
                      </div>
                      <div style={{fontSize:11,color:'rgba(26,23,20,0.45)'}}>Mark {isMultiCity ? "them" : "it"} as visited on your scratch map</div>
                    </div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#FAF6F0',borderRadius:10}}>
                    <span style={{fontSize:20}}>📍</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:'#1A1714'}}>Share a local tip</div>
                      <div style={{fontSize:11,color:'rgba(26,23,20,0.45)'}}>Help the next traveler discover something great · +50 pts</div>
                    </div>
                  </div>
                </div>
              </div>
              <button className="pt-primary-btn" onClick={() => setStep(2)}>Let's go →</button>
              <button className="pt-secondary-btn" onClick={onSkip}>Skip for now</button>
            </div>
          </>
        )}

        {/* Step 2 — Scratch map */}
        {step === 2 && (
          <>
            <div className="pt-hero">
              <span className="pt-emoji">🗺️</span>
              <h1 className="pt-hero-title">
                {isMultiCity
                  ? <>Scratch off <em>{allCountries.length} countries</em></>
                  : <>Scratch off <em>{trip.country}</em></>
                }
              </h1>
              <p className="pt-hero-sub">Add {isMultiCity ? "them" : "it"} to your world scratch map</p>
            </div>
            <div className="pt-content">
              <div className="pt-steps">
                <div className="pt-step-dot active"></div>
                <div className="pt-step-dot"></div>
                <div className="pt-step-dot"></div>
              </div>
              <div style={{height:16}}></div>

              {isMultiCity ? (
                <div className="pt-card">
                  <div className="pt-card-title">You visited {allCountries.length} countries! 🎉</div>
                  <div className="pt-card-sub">Scratch each country off your map, or do them all at once.</div>

                  <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:14}}>
                    {allCountries.map(country => (
                      <div key={country} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#FAF6F0',borderRadius:10}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{fontSize:16}}>
                            {scratchedCountries.has(country) ? "✅" : "🗺️"}
                          </span>
                          <span style={{fontSize:13,fontWeight:500,color:'#1A1714'}}>{country}</span>
                          {!COUNTRY_CODES[country] && (
                            <span style={{fontSize:10,color:'rgba(26,23,20,0.4)'}}>manual</span>
                          )}
                        </div>
                        {scratchedCountries.has(country) ? (
                          <span style={{fontSize:11,padding:'2px 10px',borderRadius:100,background:'#E1F5EE',color:'#085041',fontWeight:500}}>Scratched</span>
                        ) : (
                          <button
                            onClick={() => handleScratchOne(country)}
                            style={{fontSize:11,padding:'5px 12px',borderRadius:100,background:'#FEF0DC',color:'#C47A0A',border:'none',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",fontWeight:500}}
                          >
                            Scratch ✨
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {!scratchAllDone ? (
                    <button className="pt-scratch-btn" onClick={handleScratchAll}>
                      ✨ Scratch all {allCountries.length} countries!
                    </button>
                  ) : (
                    <div className="pt-scratch-done">
                      ✅ All {allCountries.length} countries scratched!
                    </div>
                  )}
                </div>
              ) : (
                <div className="pt-card">
                  <div className="pt-card-title">You visited {trip.country}! 🎉</div>
                  <div className="pt-card-sub">
                    {countryCode
                      ? `Tap below to add ${trip.country} to your personal scratch map. It'll show up highlighted the next time you open your journal.`
                      : `We couldn't automatically match ${trip.country} to the map — but you can add it manually in your journal!`}
                  </div>
                  {!scratched ? (
                    <button className="pt-scratch-btn" onClick={handleScratch}>
                      ✨ Scratch off {trip.country}
                    </button>
                  ) : (
                    <div className="pt-scratch-done">
                      ✅ {trip.country} scratched off!
                    </div>
                  )}
                </div>
              )}

              {scratchError && (
                <p style={{fontSize:13,color:'#c9622f',background:'#fef0ed',border:'1px solid rgba(201,98,47,0.2)',borderRadius:10,padding:'10px 14px',marginBottom:8}}>
                  {scratchError}
                </p>
              )}
              <button className="pt-primary-btn" onClick={() => setStep(3)}>
                {scratched ? "Next — rate your trip →" : "Skip this →"}
              </button>
            </div>
          </>
        )}

        {/* Step 3 — Rating */}
        {step === 3 && (
          <>
            <div className="pt-hero">
              <span className="pt-emoji">⭐</span>
              <h1 className="pt-hero-title">How was <em>{trip.destination}?</em></h1>
              <p className="pt-hero-sub">Rate your trip so we can improve your future itineraries</p>
            </div>
            <div className="pt-content">
              <div className="pt-steps">
                <div className="pt-step-dot active"></div>
                <div className="pt-step-dot active"></div>
                <div className="pt-step-dot"></div>
              </div>
              <div style={{height:16}}></div>
              <div className="pt-card" style={{textAlign:'center'}}>
                <div className="pt-card-title">Overall experience</div>
                <div className="pt-card-sub">Tap a star to rate your trip</div>
                <div className="pt-stars">
                  {[1,2,3,4,5].map(i => (
                    <button
                      key={i}
                      className={`pt-star-btn${i <= rating ? " filled" : ""}${poppingStar === i ? " popping" : ""}`}
                      onClick={() => { setRating(i); setPoppingStar(i); }}
                      onAnimationEnd={() => setPoppingStar(0)}
                    >
                      {i <= rating ? "★" : "☆"}
                    </button>
                  ))}
                </div>
              </div>
              {rateError && (
                <p style={{fontSize:13,color:'#c9622f',background:'#fef0ed',border:'1px solid rgba(201,98,47,0.2)',borderRadius:10,padding:'10px 14px',marginBottom:8}}>
                  {rateError}
                </p>
              )}
              <button className="pt-primary-btn" onClick={handleRate} disabled={rating === 0}>
                {rating === 0 ? "Select a rating" : "Save rating →"}
              </button>
              <button className="pt-secondary-btn" onClick={() => setStep(4)}>Skip for now</button>
            </div>
          </>
        )}

        {/* Step 4 — Local tip */}
        {step === 4 && (
          <>
            <div className="pt-hero">
              <span className="pt-emoji">📍</span>
              <h1 className="pt-hero-title">Share what you <em>discovered</em></h1>
              <p className="pt-hero-sub">Help the next traveler heading to {trip.destination}</p>
            </div>
            <div className="pt-content">
              <div className="pt-steps">
                <div className="pt-step-dot active"></div>
                <div className="pt-step-dot active"></div>
                <div className="pt-step-dot active"></div>
              </div>
              <div style={{height:16}}></div>

              <div className="pt-points-badge">
                ⭐ Share a tip and earn +50 points
              </div>

              <div className="pt-card">
                <div className="pt-field">
                  <label className="pt-label">Tip title</label>
                  <input className="pt-input" placeholder="e.g. Best KBBQ spot in the city" value={tip.title} onChange={e => setTip({...tip, title: e.target.value})} />
                </div>
                <div className="pt-field">
                  <label className="pt-label">Your tip</label>
                  <textarea className="pt-textarea" placeholder="What should the next traveler know? The more specific the better..." value={tip.tip} onChange={e => setTip({...tip, tip: e.target.value})} />
                </div>
                <div className="pt-field">
                  <label className="pt-label">Category</label>
                  <div className="pt-categories">
                    {CATEGORIES.map(cat => (
                      <button key={cat} className={`pt-cat ${tip.category === cat ? "selected" : ""}`} onClick={() => setTip({...tip, category: cat})}>{cat}</button>
                    ))}
                  </div>
                </div>
              </div>

              {tipError && (
                <p style={{fontSize:13,color:'#c9622f',marginTop:8}}>{tipError}</p>
              )}
              <button className="pt-primary-btn" onClick={handleSubmitTip} disabled={submittingTip || !tip.title || !tip.tip}>
                {submittingTip ? "Sharing..." : "Share tip & earn 50 pts →"}
              </button>
              <button className="pt-secondary-btn" onClick={() => setStep(5)}>Skip tip</button>
            </div>
          </>
        )}

        {/* Step 5 — Done */}
        {step === 5 && (
          <>
            <div className="pt-hero">
              <span className="pt-emoji">☀️</span>
              <h1 className="pt-hero-title">All done!<br /><em>See you next trip.</em></h1>
              <p className="pt-hero-sub">Thanks for being part of the Solis community</p>
            </div>
            <div className="pt-content">
              <div className="pt-card" style={{textAlign:'center',padding:32}}>
                <div className="pt-done-icon">🎉</div>
                <div style={{fontFamily:'Playfair Display,serif',fontSize:20,fontWeight:600,color:'#1A1714',marginBottom:8}}>{trip.destination} — done!</div>
                <div style={{fontSize:14,color:'rgba(26,23,20,0.5)',lineHeight:1.6,marginBottom:20}}>
                  Your trip is wrapped up. Check your journal and scratch map to see your progress.
                </div>
                <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
                  {isMultiCity
                    ? scratchedCountries.size > 0 && (
                        <span style={{fontSize:12,padding:'4px 12px',borderRadius:100,background:'#E1F5EE',color:'#085041',fontWeight:500}}>
                          ✓ {scratchedCountries.size} {scratchedCountries.size === 1 ? "country" : "countries"} scratched
                        </span>
                      )
                    : scratched && <span style={{fontSize:12,padding:'4px 12px',borderRadius:100,background:'#E1F5EE',color:'#085041',fontWeight:500}}>✓ Map scratched</span>
                  }
                  {tip.title && <span style={{fontSize:12,padding:'4px 12px',borderRadius:100,background:'#FEF0DC',color:'#C47A0A',fontWeight:500}}>✓ Tip shared · +50 pts</span>}
                </div>
              </div>
              <button className="pt-primary-btn" onClick={onComplete}>Back to my trips →</button>
            </div>
          </>
        )}

      </div>
    </>
  );
}